/**
 * Shared Formatting Utilities — Central place for locale-aware formatting.
 *
 * All pages should use these instead of hardcoding `₹` / `en-IN`.
 * Each function accepts an optional CountryConfig; if omitted, defaults to India.
 *
 * @since 2.0.0 — International
 */
import { getCountryConfig, type CountryConfig } from '@/shared/config/countryConfig';

// ─── Default Config ──────────────────────────────────────────────
const DEFAULT_CC = getCountryConfig('IN');

// ─── Currency Formatting ─────────────────────────────────────────

/** Format a number as currency with the country's symbol and locale, e.g. ₹1,23,456.00 */
export function formatCurrency(val: number, cc: CountryConfig = DEFAULT_CC): string {
    return `${cc.currencySymbol}${val.toLocaleString(cc.numberFormat, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

/** A figure on the FACE of a financial statement, where nothing else carries
 *  the sign. A negative comes back in brackets, which is the accountant's minus
 *  and what Schedule III expects: `(₹14,865.70)`.
 *
 *  Use this, never `formatCurrency(Math.abs(x))`, for any statement line.
 *  A line running CONTRARY to its own side is the one a reader most needs to
 *  see, and stripping the sign is how it gets hidden: an accumulated deficit
 *  printed under Shareholders' funds as though it were a positive reserve, a
 *  credit balance in Trade receivables, a debit in Trade payables. Totals are
 *  computed on the signed value, so the statement keeps footing while its own
 *  lines cannot be added up by eye.
 *
 *  Morphix-House, 31 Aug 2026: share capital 15,000 + "reserves" 14,865.70 +
 *  liabilities 1,15,000 printed against a total of 1,15,134.30 — only true if
 *  that reserves figure is negative. It read as the books being wrong. They
 *  were not; the rendering was.
 *
 *  Zero prints as zero, not a dash: a line that exists and is nil is a fact,
 *  while a blank on a balance sheet reads as "not applicable".
 *
 *  Careful: `-0.001` rounds to zero and must NOT come back as `(₹0.00)`, so the value
 *  is rounded to paise BEFORE its sign is read. The `+ 0` then collapses IEEE
 *  negative zero — `Math.round(-0.1)` is `-0`, which is not `< 0`, so it slips
 *  past the bracket and `toLocaleString` prints it as `-0.00`: a minus sign on
 *  a nil line, which reads as a fault in the statement. */
export function formatStatementAmount(val: number, cc: CountryConfig = DEFAULT_CC): string {
    const rounded = Math.round(val * 100) / 100 + 0;
    return rounded < 0 ? `(${formatCurrency(-rounded, cc)})` : formatCurrency(rounded, cc);
}

/** Compact currency for dashboard KPIs: ₹1.5 Cr, $2.3M, etc. */
export function formatCurrencyCompact(amount: number, cc: CountryConfig = DEFAULT_CC): string {
    const abs = Math.abs(amount);
    const sym = cc.currencySymbol;

    // Indian numbering: lakhs and crores
    if (cc.numberFormat === 'en-IN') {
        if (abs >= 10_000_000) return `${sym}${(amount / 10_000_000).toFixed(2)} Cr`;
        if (abs >= 100_000) return `${sym}${(amount / 100_000).toFixed(2)} L`;
        return `${sym}${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }

    // Western numbering: K, M, B
    if (abs >= 1_000_000_000) return `${sym}${(amount / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${sym}${(amount / 1_000).toFixed(1)}K`;
    return `${sym}${amount.toLocaleString(cc.numberFormat, { maximumFractionDigits: 0 })}`;
}

/** Compact number for chart tick labels: 1.5L / 2.3M etc. */
export function compactNumber(value: number, cc: CountryConfig = DEFAULT_CC): string {
    if (cc.numberFormat === 'en-IN') {
        if (value >= 100_000) return `${(value / 100_000).toFixed(1)}L`;
        if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
        return value.toString();
    }
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
    return value.toString();
}

// ─── Date Formatting ─────────────────────────────────────────────

/**
 * A calendar date as `YYYY-MM-DD` — the wire format, for storing and comparing.
 *
 * Uses **local** date parts on purpose. `toISOString()` converts to UTC first, and
 * a date built at local midnight in IST (UTC+5:30) becomes 18:30 the *previous*
 * day — so a GST return due on the 20th would be stored as the 19th, and every
 * deadline computed from it would be a day early.
 *
 * This is the machine-readable twin of `formatDate`, which is for human eyes and
 * always carries a month name.
 */
export function toIsoDate(d: Date | string | number): string {
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/**
 * Format a date as "dd MMM yyyy" in the country's locale.
 *
 * Always month-name, never numeric: "04/01/2020" reads as 4 Jan or 1 Apr
 * depending on who's looking. Prefer this over `toLocaleDateString()`.
 */
export function formatDate(
    dateStr: string | Date | number,
    cc: CountryConfig = DEFAULT_CC,
    opts?: Intl.DateTimeFormatOptions,
): string {
    const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(cc.numberFormat, opts ?? {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

/**
 * A moment, not a day: "10 Apr 2025, 3:42 pm". For "who did this, and when",
 * where the day alone does not say which of two edits came first. Month-name,
 * like formatDate.
 */
export function formatDateTime(dateStr: string | Date | number, cc: CountryConfig = DEFAULT_CC): string {
    const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(cc.numberFormat, {
        day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

/** Format a date as short: "dd Mon" */
export function formatDateShort(dateStr: string, cc: CountryConfig = DEFAULT_CC): string {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(cc.numberFormat, { day: '2-digit', month: 'short' });
}

// ─── Number Formatting ───────────────────────────────────────────

/** Format a plain number with locale grouping (no currency symbol) */
export function formatNumber(
    val: number,
    cc: CountryConfig = DEFAULT_CC,
    opts?: Intl.NumberFormatOptions,
): string {
    return val.toLocaleString(cc.numberFormat, opts);
}

// ─── Config lookup helper for components ─────────────────────────

/** Derive CountryConfig from an org object. Handles both code and name. */
export function getOrgCountryConfig(currentOrg: unknown): CountryConfig {
    const orgObj = currentOrg as Record<string, unknown> | undefined;
    const country = typeof orgObj?.country === 'string' ? orgObj.country : 'IN';
    return getCountryConfig(country);
}

/**
 * The month a business's financial year opens in, by the server's own rule
 * (`org_fy_start_month`, migration 00258): a saved month other than the schema
 * default of 4 was chosen on purpose and wins; 4, blank or out of range defers to
 * the country's statutory year. Every FY boundary on a screen goes through this,
 * so a screen and the compliance facts never disagree about where the year starts.
 */
export function orgFyStartMonth(currentOrg: unknown): number {
    const saved = Number((currentOrg as Record<string, unknown> | undefined)?.fy_start_month);
    if (Number.isInteger(saved) && saved >= 1 && saved <= 12 && saved !== 4) return saved;
    return getOrgCountryConfig(currentOrg).fyStartMonth;
}

/**
 * fystart: a financial year other than April to March saved on an Indian business.
 * A company's year is April to March by law (Companies Act 2013, section 2(41)),
 * and the tax year is the same for every business. On 7 Oct 2026 five live Indian
 * companies were found saved with January, July or September, and every report
 * believed them, so a screen that asks or shows the month says so and offers April.
 */
export function isUnusualIndianFy(currentOrg: unknown, month: number): boolean {
    return getOrgCountryConfig(currentOrg).code === 'IN' && month !== 4;
}
